/**
 * What light comes from: the object (an arrow or a gradient rod), a point
 * source, a parallel beam and a ray box.
 *
 * None of these has a surface — light passes through an object as if it were
 * not there. The object and the point source present POINTS to be imaged; the
 * beam and the ray box EMIT rays of their own.
 */
import { Emission, ObjectPoint, PartKind, numbered } from '../registry';
import { box, disc, localToWorld } from '../shapes';
import { Control, Part, RayMode, Vec2, World } from '../types';
import { add, fromAngle, perp, rad, scale } from '../vec';

export interface ObjectData {
    form: 'rod' | 'arrow';
    /** cm, end A to end B. */
    length: number;
    /** Degrees from upright, clockwise: ±90 lies along the axis. */
    tilt: number;
    /** The points rays are drawn from, as fractions of the way from A to B.
     *  Derived from `form` and `rayPoints`; set directly only by presets. */
    traced: number[];
    /** How many points of the rod rays are drawn from: '1' to '4'. */
    rayPoints?: RayPoints;
    /** Textbook construction rays, or a fan of `rays` from each traced point. */
    rayMode?: RayMode;
    rays?: number;
    palette: string;
    /** nm: the arrow's one colour — its rays, itself and its image. The rod is
     *  coloured by its palette instead, point by point, and ignores this. */
    wavelength?: number;
    [key: string]: unknown;
}

export interface PointSourceData { spread: number; rays: number; rayMode?: RayMode; wavelength?: number; [key: string]: unknown }

/** The light from a lamp, a beam or a ray box: one colour each, set on the source. */
export const DEFAULT_WAVELENGTH = 589;
const wavelengthControl: Control = { type: 'wavelength', key: 'wavelength', label: 'Colour' };
/** The ray box and the beam may also shine white; a lamp and the arrow may not. */
const colourOrWhite: Control = { ...wavelengthControl, white: true };

/**
 * White light, as traced: red, green and violet — enough to show the order of
 * the spectrum and its spread, without seven rays crowding every one.
 */
export const WHITE_LIGHT = [650, 530, 410] as const;
/** The one of the three the working, the normals and the angles are written for. */
export const WHITE_LEAD = 530;

/** Whether a part shines white. */
export const isWhite = (p: Part): boolean => (p.data as { white?: unknown }).white === true;

/** The wavelength a part's light is, nm. A source saved before sources had their
 *  own colour has none, and shines in the bench's old single colour. */
export function wavelengthOf(p: Part, w: World): number {
    const own = (p.data as { wavelength?: unknown }).wavelength;
    return typeof own === 'number' && Number.isFinite(own) ? own : w.light.wavelengths[0] ?? DEFAULT_WAVELENGTH;
}

/** The two ray controls every imaging source shares: which rays, and how many in a fan. */
function rayControls(mode: RayMode | undefined, countLabel: string): Control[] {
    return [
        {
            type: 'choice', key: 'rayMode', label: 'Rays',
            choices: [{ value: 'textbook', label: 'textbook rays' }, { value: 'fan', label: 'a fan' }],
        },
        ...(mode === 'fan' ? [{ type: 'number' as const, key: 'rays', label: countLabel, min: 2, max: 40, step: 1 }] : []),
    ];
}
export interface BeamData {
    width: number;
    rays: number;
    wavelength?: number;
    /** White light: each ray is three, one per colour of `WHITE_LIGHT`. */
    white?: boolean;
    [key: string]: unknown;
}
export interface RayBoxData {
    /** How many parallel rays leave it, as through a ray box's slit plate. */
    rays: number;
    /** cm between neighbouring rays. */
    spacing: number;
    /** nm; absent on a ray box saved before sources had their own colour. */
    wavelength?: number;
    /** White light: each ray is three, one per colour of `WHITE_LIGHT`. Its
     *  `wavelength` is kept for when white is turned off. */
    white?: boolean;
    [key: string]: unknown;
}

/** How many points along an extended object are imaged before refinement (§8.1). */
export const OBJECT_SAMPLES = 33;

/** The point a fraction `t` of the way from end A to end B. */
export function objectPoint(p: Part<ObjectData>, t: number): Vec2 {
    const tilt = rad(p.data.tilt);
    return add(p.at, scale({ x: Math.sin(tilt), y: Math.cos(tilt) }, p.data.length * t));
}

/**
 * Which points of the rod rays come from. One is the tip, B. Two adds the
 * MIDDLE rather than A: on an upright rod A sits on the axis, so its rays all
 * run along the axis and a second point there would show nothing new. Three is
 * both ends and the middle, which is when A's image on the axis earns its place.
 * Four is both ends and the thirds between them.
 */
export type RayPoints = '1' | '2' | '3' | '4';
export const RAY_POINTS: Record<RayPoints, number[]> = {
    '1': [1], '2': [0.5, 1], '3': [0, 0.5, 1], '4': [0, 1 / 3, 2 / 3, 1],
};

const pointLabel = (t: number): string => (t === 0 ? 'A' : t === 1 ? 'B' : `${Math.round(t * 100)}% of the way from A`);

export const object: PartKind<ObjectData> = {
    key: 'object',
    label: 'Object',
    tag: 'O',
    freedom: { onAxis: true, turns: false },

    create: (at: Vec2, id: string): Part<ObjectData> => ({
        id, kind: 'object', at: { x: at.x, y: 0 }, angle: 0, mount: 'axis',
        data: { form: 'rod', length: 5, tilt: 0, traced: RAY_POINTS['3'], rayPoints: '3', rayMode: 'textbook', rays: 7, palette: 'deep', wavelength: DEFAULT_WAVELENGTH },
    }),

    surfaces: () => [],

    points(p): ObjectPoint[] {
        const ts = new Set<number>();
        for (let i = 0; i < OBJECT_SAMPLES; i++) ts.add(i / (OBJECT_SAMPLES - 1));
        for (const t of p.data.traced) ts.add(t);
        const traced = new Set(p.data.traced);
        return [...ts].sort((a, b) => a - b).map(t => ({
            id: `t${t.toFixed(4)}`, at: objectPoint(p, t), label: pointLabel(t), t, traced: traced.has(t),
        }));
    },

    outline(p) {
        const a = p.at, b = objectPoint(p, 1);
        const n = scale(perp(fromAngle(Math.atan2(b.y - a.y, b.x - a.x))), 0.15);
        return [add(a, n), add(b, n), add(b, scale(n, -1)), add(a, scale(n, -1))];
    },

    controls: (p) => [
        {
            type: 'choice', key: 'form', label: 'Form',
            choices: [{ value: 'rod', label: 'gradient rod' }, { value: 'arrow', label: 'arrow' }],
        },
        // The arrow is one colour; the rod's gradient is how it shows inversion.
        ...(p.data.form === 'arrow' ? [wavelengthControl] : []),
        { type: 'number', key: 'length', label: 'Length', unit: 'cm', min: 0.5, max: 20, step: 0.5 },
        { type: 'number', key: 'tilt', label: 'Tilt from upright', unit: '°', min: -90, max: 90, step: 1 },
        ...rayControls(p.data.rayMode, 'Rays per point'),
        // A rod only: the arrow is the textbook's figure, and the textbook draws from its tip.
        ...(p.data.form === 'rod' ? [
            {
                type: 'choice' as const, key: 'rayPoints', label: 'Rays from how many points',
                choices: [
                    { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' },
                ],
            },
            {
                type: 'choice' as const, key: 'palette', label: 'Colours',
                choices: [{ value: 'deep', label: 'blue → amber' }, { value: 'sea', label: 'teal → coral' }, { value: 'mono', label: 'dark → light' }],
            },
        ] : []),
    ],

    // The arrow traces its tip; the rod traces the points `rayPoints` names.
    derive: (p) => ({ traced: p.data.form === 'arrow' ? [1] : RAY_POINTS[p.data.rayPoints ?? '3'] }),

    name: (p, w) => numbered(p as Part, w, 'O'),
};

export const pointSource: PartKind<PointSourceData> = {
    key: 'pointSource',
    label: 'Point source',
    tag: 'S',
    freedom: { onAxis: true, turns: true },
    create: (at: Vec2, id: string): Part<PointSourceData> => ({
        id, kind: 'pointSource', at: { x: at.x, y: 0 }, angle: 0, mount: 'axis', data: { spread: 20, rays: 7, rayMode: 'fan', wavelength: DEFAULT_WAVELENGTH },
    }),
    surfaces: () => [],
    points: (p) => [{ id: 'p', at: p.at, label: 'the source', traced: true }],
    /** With nothing in front of it to aim at, a fan of its own spread. */
    emit(p): Emission[] {
        const n = Math.max(1, Math.round(p.data.rays));
        const half = rad(p.data.spread);
        return Array.from({ length: n }, (_, i) => ({
            from: p.at,
            direction: fromAngle(p.angle + (n === 1 ? 0 : -half + (2 * half * i) / (n - 1))),
            point: 'p', role: 'fan' as const,
        }));
    },
    outline: (p) => localToWorld(p as Part, disc(0.5)),
    // With a lens or mirror in front of it, the choice of rays applies; with
    // nothing to aim at, it sends a fan of `rays` over its own spread.
    controls: (p) => [
        wavelengthControl,
        ...rayControls(p.data.rayMode, 'Rays in the fan'),
        { type: 'number', key: 'spread', label: 'Spread with nothing in front (either side)', unit: '°', min: 1, max: 90, step: 1 },
    ],
    name: (p, w) => numbered(p as Part, w, 'S'),
};

export const parallelBeam: PartKind<BeamData> = {
    key: 'parallelBeam',
    label: 'Parallel beam',
    tag: 'B',
    freedom: { onAxis: true, turns: true },
    create: (at: Vec2, id: string): Part<BeamData> => ({
        id, kind: 'parallelBeam', at, angle: 0, mount: 'free', data: { width: 6, rays: 7, wavelength: DEFAULT_WAVELENGTH },
    }),
    surfaces: () => [],
    emit(p): Emission[] {
        const n = Math.max(1, Math.round(p.data.rays));
        const dir = fromAngle(p.angle);
        const across = perp(dir);
        return Array.from({ length: n }, (_, i) => ({
            from: add(p.at, scale(across, n === 1 ? 0 : -p.data.width / 2 + (p.data.width * i) / (n - 1))),
            direction: dir, point: `b${i}`, role: 'fan' as const,
        }));
    },
    outline: (p) => localToWorld(p as Part, box(-1, 1, p.data.width / 2 + 0.3)),
    controls: () => [
        colourOrWhite,
        { type: 'number', key: 'width', label: 'Beam width', unit: 'cm', min: 1, max: 20, step: 0.5 },
        { type: 'number', key: 'rays', label: 'Rays', min: 1, max: 40, step: 1 },
    ],
    name: (p, w) => numbered(p as Part, w, 'B'),
};

export const rayBox: PartKind<RayBoxData> = {
    key: 'rayBox',
    label: 'Ray box',
    tag: 'R',
    freedom: { onAxis: false, turns: true },
    create: (at: Vec2, id: string): Part<RayBoxData> => ({
        id, kind: 'rayBox', at, angle: 0, mount: 'free', data: { rays: 1, spacing: 0.5, wavelength: DEFAULT_WAVELENGTH },
    }),
    surfaces: () => [],
    /**
     * Parallel rays, centred on the box's aim. The middle one is `point: 'ray'` —
     * the one normals and angles are drawn for, since on nine rays they would
     * bury the drawing — and the others are numbered out from it.
     */
    emit(p): Emission[] {
        const n = Math.max(1, Math.round(p.data.rays ?? 1));
        const dir = fromAngle(p.angle);
        const across = perp(dir);
        const spacing = p.data.spacing ?? 0.5;
        return Array.from({ length: n }, (_, i) => {
            const k = i - (n - 1) / 2;
            return {
                from: add(p.at, scale(across, k * spacing)), direction: dir,
                point: k === 0 ? 'ray' : `ray${k > 0 ? '+' : ''}${k}`, role: 'single' as const,
            };
        });
    },
    outline: (p) => localToWorld(p as Part, box(-1.5, 1.5, Math.max(0.8, ((Math.round(p.data.rays ?? 1) - 1) * (p.data.spacing ?? 0.5)) / 2 + 0.4))),
    controls: () => [
        colourOrWhite,
        { type: 'number', key: 'rays', label: 'Rays', min: 1, max: 9, step: 1 },
        { type: 'number', key: 'spacing', label: 'Spacing between rays', unit: 'cm', min: 0.1, max: 3, step: 0.1 },
    ],
    name: (p, w) => numbered(p as Part, w, 'R'),
};
